import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  dateValue,
  dayRangeValue,
  QueryFieldMapping,
} from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { InputObject } from "../controls/default";

export interface CompanyViewCrumb {
  readonly key: string;
  readonly title: string;
  readonly request: string;
}

export interface CompanyViewForm {
  readonly name: string;
  readonly address: string;
  readonly phoneNumber: string;
  readonly domain: InputObject;
  readonly email: string;
  // Parent Company and Created On are both shown as sortable columns; these make them filterable too,
  // so the grid can answer "who sits under this parent" and "who joined in this window" without an export.
  readonly parentCompany: InputObject;
  readonly createdOn: any[];
}

export interface DepartmentViewForm {
  readonly name: string;
}

export interface UsersViewForm {
  readonly company?: InputObject;
  readonly role?: InputObject;
  readonly department?: InputObject;
  readonly email?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly dateOfBirth?: string;
}

export const companiesViewColumns: DataColumn[] = [
  {
    title: "Name",
    key: "name",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Address",
    key: "address",
    width: 192,
    sort: true,
  },
  {
    title: "Email",
    key: "email",
    width: 192,
    sort: true,
  },
  {
    title: "Phone Number",
    key: "phoneNumber",
    width: 192,
    sort: true,
  },
  {
    title: "Industry",
    key: "domainName",
    width: 192,
    sort: true,
  },
  {
    title: "Parent Company",
    key: "parentCompanyName",
    width: 192,
    sort: true,
  },
  // Admin requirement 2026-11-03 — Status + Activation Date visible on
  // every management grid (so operators can see at a glance which rows
  // are inactive and when they went inactive).
  {
    title: "Status",
    key: "isActive",
    width: 110,
    sort: true,
    transform: (v: boolean) => (v === false ? "Inactive" : "Active"),
  },
  {
    title: "Activated On",
    key: "activatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Deactivated On",
    key: "deactivatedAt",
    width: 148,
    format: "DD/MM/YYYY",
    sort: true,
  },
  // Admin brief 2026-11-04 — business lifecycle column, separate from
  // Status/Active above. Values come from the backend AccountStatus enum
  // (0=Pending, 1=Verified, 2=Trial, 3=Suspended).
  {
    title: "Account Status",
    key: "accountStatus",
    width: 140,
    sort: true,
    transform: (v: number) =>
      v === 1 ? "Verified" : v === 2 ? "Trial" : v === 3 ? "Suspended" : "Pending",
  },
  // PD-01 §3.3 lists creation date among the grid's key fields. `createdOn` was already on CompanyDTO
  // (via StoreableDTO) and already on the wire — only the column was missing. Placed with the company's
  // own attributes rather than after the count badges, which are a different kind of value.
  {
    title: "Created On",
    key: "createdOn",
    width: 128,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Sub Companies",
    key: "subCompaniesCount",
    width: 192,
    sort: true,
    type: "badge",
  },
  {
    title: "Departments",
    key: "departmentsCount",
    width: 192,
    sort: true,
    type: "badge",
  },
  {
    title: "Users",
    key: "usersCount",
    width: 192,
    sort: true,
    type: "badge",
  },
  {
    title: "Assets",
    key: "assetsCount",
    width: 192,
    sort: true,
    type: "badge",
  },
];

export const companiesDepartmentViewColumns: DataColumn[] = [
  {
    title: "Name",
    key: "name",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Users",
    key: "usersCount",
    width: 192,
    sort: true,
    type: "badge",
  },
];

export const companiesUserViewColumns: DataColumn[] = [
  {
    title: "User Name",
    key: "userName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "First Name",
    key: "firstName",
    width: 192,
    sort: true,
  },
  {
    title: "Last Name",
    key: "lastName",
    width: 192,
    sort: true,
  },
  {
    title: "Middle Name",
    key: "middleName",
    width: 192,
    sort: true,
  },
  {
    title: "Date Of Birth",
    key: "dateOfBirth",
    width: 128,
    format: "DD/MM/YYYY",
    sort: true,
  },
  {
    title: "Email",
    key: "email",
    width: 256,
    sort: true,
  },
  {
    title: "Phone",
    key: "phoneNumber",
    width: 192,
    sort: true,
  },
  {
    title: "Email Confirmed",
    key: "emailConfirmed",
    width: 128,
    sort: true,
  },
  {
    title: "Company Name",
    key: "companyName",
    width: 192,
    sort: true,
  },
  {
    title: "Role",
    key: "roleName",
    width: 192,
    sort: true,
  },
  {
    title: "Tenant",
    key: "tenantName",
    width: 192,
    sort: true,
  },
  {
    title: "Department",
    key: "departmentName",
    width: 192,
    sort: true,
  },
];

const companiesQueryMappings: QueryFieldMapping[] = [
  { formField: "name" },
  { formField: "email" },
  { formField: "address" },
  { formField: "phoneNumber" },
  { formField: "domain", queryField: "domainUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "parentCompany", queryField: "parentCompanyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  // dayRangeValue, not dateRangeValue: the picker is date-only, so the raw upper bound would be midnight
  // and would drop every company registered later on the closing day.
  { formField: "createdOn", operation: QueryOperation.Between, transform: dayRangeValue },
];

export const mapCompaniesViewQuery = (values: CompanyViewForm): SearchQuery =>
  buildSearchQuery(values, companiesQueryMappings);

const departmentsQueryMappings: QueryFieldMapping[] = [
  { formField: "name" },
];

export const mapDepartmentsViewQuery = (values: CompanyViewForm): SearchQuery =>
  buildSearchQuery(values, departmentsQueryMappings);

const companyUsersQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "role", queryField: "roleUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "department", queryField: "departmentUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "email" },
  { formField: "firstName" },
  { formField: "lastName" },
  { formField: "dateOfBirth", operation: QueryOperation.Equal, transform: dateValue },
];

export const mapUsersViewQuery = (values: CompanyViewForm): SearchQuery =>
  buildSearchQuery(values, companyUsersQueryMappings);
